import { Box, Tooltip, IconButton } from "@mui/material";
import { Chip } from "@avenue-8/sidekick-ui";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import styled from "@emotion/styled";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useRef, useState, useEffect } from "react";
import type { Swiper as SwiperType } from "swiper";

const CarouselContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
`;

const SwiperContainer = styled(Box)`
  flex: 1;
  overflow: hidden;

  .swiper {
    overflow: visible;
  }

  .swiper-slide {
    width: auto;
  }

  .swiper-button-next,
  .swiper-button-prev {
    display: none;
  }
`;

const NavigationButton = styled(IconButton)`
  position: absolute;
  z-index: 5;
  width: 36px;
  height: 36px;
  background-color: ${(p) => p.theme.colors.background};
  border: 1px solid ${(p) => p.theme.colors.border2};
  border-radius: 0.5rem;
  flex-shrink: 0;
`;

const NavigationButtonPrev = styled(NavigationButton)`
  left: 0;
`;

const NavigationButtonNext = styled(NavigationButton)`
  right: 0;
`;

interface AppliedFiltersChipsProps {
  filters: Array<{ id: string; label: string; removable?: boolean }>;
  onRemoveFilter?: (filterId: string) => void;
}

export const AppliedFiltersChips = ({
  filters,
  onRemoveFilter,
}: AppliedFiltersChipsProps) => {
  const swiperRef = useRef<SwiperType | null>(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.slideTo(0, 0);
      setIsBeginning(true);
      setIsEnd(swiperRef.current.isEnd);
    }
  }, [filters]);

  const updateButtonStates = () => {
    if (swiperRef.current) {
      setIsBeginning(swiperRef.current.isBeginning);
      setIsEnd(swiperRef.current.isEnd);
    }
  };

  const handlePrevClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    swiperRef.current?.slidePrev();
    setTimeout(updateButtonStates, 50);
  };

  const handleNextClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    swiperRef.current?.slideNext();
    setTimeout(updateButtonStates, 50);
  };

  const handleSlideChange = () => {
    updateButtonStates();
  };

  return (
    <Box mt={2} mx={2}>
      <CarouselContainer
        sx={{
          paddingRight: isEnd ? 0 : "2.75rem",
          paddingLeft: isBeginning ? 0 : "2.75rem",
        }}
      >
        {!isBeginning && (
          <NavigationButtonPrev onClick={handlePrevClick} size="small">
            <ChevronLeftIcon fontSize="small" />
          </NavigationButtonPrev>
        )}

        <SwiperContainer>
          <Swiper
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
              updateButtonStates();
            }}
            onSlideChange={handleSlideChange}
            modules={[Navigation]}
            spaceBetween={8}
            slidesPerView="auto"
            grabCursor
            freeMode
            watchSlidesProgress
          >
            {filters.map(({ id, label, removable = true }) => (
              <SwiperSlide key={id}>
                <Tooltip title={label}>
                  <Chip
                    color="primary"
                    label={label}
                    onDelete={
                      onRemoveFilter && removable
                        ? () => onRemoveFilter(id)
                        : undefined
                    }
                  />
                </Tooltip>
              </SwiperSlide>
            ))}
          </Swiper>
        </SwiperContainer>

        {!isEnd && (
          <NavigationButtonNext onClick={handleNextClick} size="small">
            <ChevronRightIcon fontSize="small" />
          </NavigationButtonNext>
        )}
      </CarouselContainer>
    </Box>
  );
};
